import "./Breadcrumb.scss";

import { AppRoutes } from "AppRoutes";
import classNames from "classnames";
import { IconHouse } from "components/Icons/IconHouse";
import { getApplicationName } from "models/Environment";
import { useBoard } from "providers";
import { useRun } from "providers";
import { useEnumLabels, useTranslations } from "providers";
import React, { useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router";

import { BreadCrumbToggleBoardSettings } from "./BreadCrumbToggleBoardSettings";

interface BreadcrumbItem {
  to: string;
  name: string;
  code?: string;
  tooltip?: string;
  isActive?: boolean;
}

export const Breadcrumb = () => {
  // attention: useParams does not work, since this component is outsides a <Routes/> component.

  const { pathname } = useLocation();
  const i18n = useTranslations();
  const enumLabels = useEnumLabels();
  const { run, runId } = useRun();
  const { board, boardId } = useBoard();
  const applicationName = getApplicationName(i18n.ApplicationName);
  let documentTitles = [applicationName];

  const breadcrumbs: BreadcrumbItem[] = [];

  if (pathname.includes(AppRoutes.VotingDone)) {
    const name = i18n.Navigation_Completed_Votes;
    documentTitles.push(name);
    breadcrumbs.push({
      to: AppRoutes.VotingDone,
      code: "vd",
      name,
      isActive: true,
    });
  }

  if (pathname.includes(AppRoutes.VotingOpen)) {
    const name = i18n.Navigation_Open_Votes;
    documentTitles.push(name);
    breadcrumbs.push({
      to: AppRoutes.VotingOpen,
      code: "vo",
      name,
      isActive: true,
    });
  }

  if (pathname.includes(AppRoutes.BoardsAll)) {
    const name = i18n.Navigation_All_Boards;
    documentTitles.push(name);
    breadcrumbs.push({
      to: AppRoutes.BoardsAll,
      name,
    });
  }

  if (pathname.includes(AppRoutes.BoardsMy)) {
    const name = i18n.Navigation_My_Boards;
    documentTitles.push(name);
    breadcrumbs.push({
      to: AppRoutes.BoardsMy,
      name,
    });
  }

  if (boardId && board) {
    const name = enumLabels.getBoardTitle(board.name);

    breadcrumbs.push({
      to: AppRoutes.toBoard(board.id),
      code: "bd",
      tooltip: i18n.Board,
      name,
    });
    documentTitles.push(name);
  }

  if (runId && run) {
    const name = enumLabels.getRunTitle(run.title);
    breadcrumbs.push({
      to: AppRoutes.toRun(run.boardId, run.id),
      code: "RE",
      tooltip: i18n.Run_Title,
      name,
    });

    documentTitles.push(name);
  }

  documentTitles = documentTitles.reverse().filter((x) => !!x);

  // Update document.title as a side effect
  useEffect(() => {
    document.title = documentTitles.join(" – ");
  });

  return (
    <div className="breadcrumb-container">
      <div className="d-flex flex-grow-1">
        <Link to="/">
          <IconHouse />
        </Link>

        {breadcrumbs.map((b, index) => {
          const id = `breadcrumb-${b.to.replace(/\//g, "-")}`;
          const anchorClass = classNames(b.isActive ? "" : "date-range-inactive");
          const textClass = classNames("text", "global-tooltip-trigger");
          const truncationClassName = index !== 0 ? "block text-truncate" : "";
          return (
            <React.Fragment key={id}>
              {index !== 0 && <span className="divider">&gt;</span>}

              <div className={truncationClassName}>
                <NavLink to={b.to} className={anchorClass}>
                  <span className={textClass} id={id} title={b.tooltip}>
                    {b.name}
                  </span>
                </NavLink>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <BreadCrumbToggleBoardSettings />
    </div>
  );
};
